import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Upload,
  FileText,
  Image as ImageIcon,
  Type,
  Tag,
  LogIn,
} from "lucide-react";
import toast, { Toaster } from "react-hot-toast";
import LogoImg from "../assets/logo.png";
import { Link } from "react-router-dom";

const ADMIN_CODE = "NAMECHILD89";

const fadeIn = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -30 },
  transition: { duration: 0.4 },
};

export const Admin = () => {
  const [isAdmin, setIsAdmin] = useState(false);
  const [code, setCode] = useState("");
  const [form, setForm] = useState({
    title: "",
    excerpt: "",
    category: "",
    cover: "",
    content: "",
  });

  const handleLogin = () => {
    if (code === ADMIN_CODE) {
      setIsAdmin(true);
      toast.success("Welcome back, Admin 👑");
    } else {
      toast.error("Invalid admin code");
    }
  };

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    const url = URL.createObjectURL(file);
    setForm({ ...form, cover: url });
    toast.success("Cover image selected");
  };

  const handleSubmit = async () => {
    if (!form.title || !form.excerpt || !form.category || !form.content) {
      toast.error("Please fill in all fields");
      return;
    }

    const date = new Date().toISOString().split("T")[0];
    const slug = form.title.toLowerCase().replace(/\s+/g, "-");
    const fileName = `${slug}.md`;

    const markdown = `---
title: "${form.title}"
date: "${date}"
excerpt: "${form.excerpt}"
category: "${form.category}"
cover: "${form.cover}"
---

${form.content}
`;

    const res = await fetch("/api/createPost", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ fileName, content: markdown }),
    });

    if (res.ok) {
      toast.success("✅ Post created successfully!");
      setForm({ title: "", excerpt: "", category: "", cover: "", content: "" });
    } else {
      toast.error("❌ Failed to create post");
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white">
      <Toaster position="top-center" />

      {/* Login Screen */}
      <AnimatePresence>
        {!isAdmin && (
          <motion.div
            {...fadeIn}
            className="flex flex-col items-center justify-center flex-1 px-6"
          >
            <div className="backdrop-blur-md bg-white/10 border border-white/20 p-8 rounded-2xl max-w-sm w-full shadow-2xl text-center">
              <motion.div
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2 }}
              >
                <Link to="/" className="flex flex-col items-center ">
                  <img src={LogoImg} alt="muhaad logo" className="h-12" />
                  <span className="text-xs">MUHAAD</span>
                </Link>
                <h1 className="text-2xl font-bold mb-2">Admin Login</h1>
                <p className="text-white/70 text-sm mb-6">
                  Admin code is required to access the blog CMS
                </p>
              </motion.div>
              <input
                type="password"
                placeholder="Enter admin code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                className="w-full p-3 mb-4 rounded-lg bg-white/10 border border-white/20 focus:ring-2 focus:ring-teal-500 outline-none"
              />
              <button
                onClick={handleLogin}
                className="w-full py-3 rounded-lg bg-teal-600 hover:bg-teal-500 transition font-semibold"
              >
                Login
              </button>
              <p className="mt-6 text-xs text-white/50">
                Powered by{" "}
                <span className="text-teal-400 font-semibold">
                  Ashita Creatives Lightweight CMS
                </span>
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Admin Dashboard */}
      {isAdmin && (
        <motion.div
          {...fadeIn}
          className="flex flex-col flex-1 max-w-5xl mx-auto py-12 px-4 w-full"
        >
          <div className="mb-10">
            <h1 className="text-4xl font-bold mb-2 just-me-again-down-here-regular">
              📝 Create a New Blog Post
            </h1>
            <p className="text-white/70 text-sm">
              Fill out the form below to publish a new article to your blog.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Left Column: Form */}
            <div className="space-y-5">
              <div>
                <label className="block text-sm mb-1 flex items-center gap-2">
                  <Type size={16} /> Title
                </label>
                <input
                  name="title"
                  value={form.title}
                  onChange={handleChange}
                  placeholder="Post title..."
                  className="w-full p-3 rounded-lg bg-white/10 border border-white/20 focus:ring-2 focus:ring-teal-500 outline-none"
                />
              </div>

              <div>
                <label className="block text-sm mb-1 flex items-center gap-2">
                  <FileText size={16} /> Excerpt
                </label>
                <input
                  name="excerpt"
                  value={form.excerpt}
                  onChange={handleChange}
                  placeholder="Short post summary..."
                  className="w-full p-3 rounded-lg bg-white/10 border border-white/20 focus:ring-2 focus:ring-teal-500 outline-none"
                />
              </div>

              <div>
                <label className="block text-sm mb-1 flex items-center gap-2">
                  <Tag size={16} /> Category
                </label>
                <input
                  name="category"
                  value={form.category}
                  onChange={handleChange}
                  placeholder="e.g. Sustainability"
                  className="w-full p-3 rounded-lg bg-white/10 border border-white/20 focus:ring-2 focus:ring-teal-500 outline-none"
                />
              </div>

              <div>
                <label className="block text-sm mb-2 flex items-center gap-2">
                  <ImageIcon size={16} /> Cover Image
                </label>
                <input
                  type="file"
                  onChange={handleFileUpload}
                  className="w-full p-2 rounded-lg bg-white/10 border border-white/20 file:mr-3 file:py-2 file:px-4 file:rounded file:border-0 file:bg-teal-600 file:text-white hover:file:bg-teal-500"
                />
                {form.cover && (
                  <img
                    src={form.cover}
                    alt="Preview"
                    className="mt-3 w-full h-48 object-cover rounded-lg shadow-lg border border-white/10"
                  />
                )}
              </div>
            </div>

            {/* Right Column: Markdown */}
            <div>
              <label className="block text-sm mb-1 flex items-center gap-2">
                <FileText size={16} /> Markdown Content
              </label>
              <textarea
                name="content"
                value={form.content}
                onChange={handleChange}
                placeholder="Write markdown content here..."
                rows={15}
                className="w-full p-3 rounded-lg bg-white/10 border border-white/20 focus:ring-2 focus:ring-teal-500 outline-none font-mono"
              />
            </div>
          </div>

          <div className="mt-8 flex justify-end">
            <button
              onClick={handleSubmit}
              className="px-8 py-3 bg-teal-600 hover:bg-teal-500 rounded-lg font-semibold shadow-lg"
            >
              🚀 Publish Post
            </button>
          </div>

          <footer className="mt-10 text-center text-xs text-white/50">
            Powered by{" "}
            <span className="text-teal-400 font-semibold">
              Ashita Creatives Light CMS
            </span>
          </footer>
        </motion.div>
      )}
    </div>
  );
};

export default Admin;
